import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Mic, PanelLeftClose, PanelLeft, Sparkle, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { PromptInput, PromptInputButton, PromptInputFooter, PromptInputSubmit, PromptInputTextarea, PromptInputTools } from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import tutorMark from "@/assets/tutor-mark.png";
import academyImg from "@/assets/bvt_academy.webp";
import bedtimeImg from "@/assets/bvt_bedtime.webp";
import practicalImg from "@/assets/bvt_practical.webp";
import { streamChat, type Msg } from "@/lib/chat-stream";
import { useAuth } from "@/contexts/AuthContext";
import { getGuestPreviewCount, setGuestPreviewCount } from "@/lib/guest-preview";
import SignupModal from "@/components/SignupModal";
import UpgradeModal from "@/components/UpgradeModal";
import { useChatCtaHref } from "@/lib/chat-cta";

const FREE_MESSAGE_LIMIT = 5;

const levels = [
  { value: "Beginner", label: "Beginner" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced", label: "Advanced" },
];

const subjects = ["Maths", "Science", "Coding", "Languages", "Writing", "Business", "Test Prep", "General"];

const learningPaths = [
  {
    name: "My AI Academy",
    blurb: "Build a learning plan around your goals",
    image: academyImg,
    prompt: "I'd like to build a personal learning plan. Ask me what I want to learn and my starting level, then outline a simple step-by-step roadmap and start our first short lesson.",
  },
  {
    name: "Bedtime Learning",
    blurb: "A gentle mini-lesson before bed",
    image: bedtimeImg,
    prompt: "Be my calm bedtime learning guide. Ask me what topic I'd like to explore tonight—or suggest 2 or 3 relaxing ideas if I'm not sure—and guide me through a gentle, easy-to-follow mini-lesson to help me wind down.",
  },
  {
    name: "Practical Skills",
    blurb: "Learn useful real-world skills",
    image: practicalImg,
    prompt: "Teach me a practical, real-world skill step by step. Ask me what I'd like to learn, start with the basics and any safety tips, and check in with me as we go.",
  },
];

const DemoSection = () => {
  const [selectedLevel, setSelectedLevel] = useState("Intermediate");
  const [selectedSubject, setSelectedSubject] = useState("General");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 768);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const { user, loading: authLoading } = useAuth();
  const ctaHref = useChatCtaHref();

  useEffect(() => {
    if (!user) setUserMessageCount(getGuestPreviewCount());
  }, [user]);

  useEffect(() => {
    if (messages.length) transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading || authLoading) return;
    if (!user && getGuestPreviewCount() >= FREE_MESSAGE_LIMIT) {
      setShowSignupModal(true);
      return;
    }

    const userMsg: Msg = { role: "user", content: text };
    setInput("");
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    let assistantSoFar = "";

    try {
      await streamChat({
        messages: [...messages, userMsg],
        ageGroup: selectedLevel,
        subject: selectedSubject,
        onDelta: (chunk) => {
          assistantSoFar += chunk;
          setMessages((prev) => {
            const last = prev.at(-1);
            return last?.role === "assistant"
              ? prev.map((message, index) => index === prev.length - 1 ? { ...message, content: assistantSoFar } : message)
              : [...prev, { role: "assistant", content: assistantSoFar }];
          });
        },
        onDone: () => {
          setIsLoading(false);
          setUserMessageCount((prev) => {
            const next = prev + 1;
            if (!user) setGuestPreviewCount(next);
            return next;
          });
          inputRef.current?.focus();
        },
        onError: (error) => {
          setIsLoading(false);
          setMessages((prev) => prev.at(-1)?.role === "user" ? prev.slice(0, -1) : prev);
          if (/sign up|log in|session/i.test(error)) setShowSignupModal(true);
        },
        onLimitReached: (info) => {
          setIsLoading(false);
          setMessages((prev) => prev.slice(0, -1));
          setUserMessageCount(info.used);
          if (!user) {
            setGuestPreviewCount(info.used);
            setShowSignupModal(true);
          } else setShowUpgradeModal(true);
        },
      });
    } catch {
      setIsLoading(false);
    }
  };

  const remaining = !user ? Math.max(0, FREE_MESSAGE_LIMIT - userMessageCount) : null;

  return (
    <section id="demo" className="h-[100dvh] overflow-hidden">
      <SignupModal open={showSignupModal} onOpenChange={setShowSignupModal} />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} />

      <div className="flex h-full">
        {/* Collapsible side panel */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-30 bg-background/70 backdrop-blur-sm md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <aside
          className={`${sidebarOpen ? "w-72" : "w-0"} fixed inset-y-0 left-0 z-40 shrink-0 overflow-hidden border-r border-border/70 bg-card/95 backdrop-blur-xl transition-[width] duration-300 md:static md:z-auto md:bg-card/40`}
        >
          <div className="flex h-full w-72 flex-col p-4">
            <div className="mb-6 flex items-center gap-2">
              <img src={tutorMark} alt="" width={32} height={32} className="h-8 w-8 object-contain" />
              <span className="text-sm font-semibold text-foreground">Best Virtual Tutor</span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="mb-6 w-full justify-start gap-2 rounded-xl border-border/80"
              onClick={() => { setMessages([]); setInput(""); inputRef.current?.focus(); }}
            >
              <Sparkle className="h-4 w-4 text-primary" /> New session
            </Button>

            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Level</div>
            <div className="mb-6 flex flex-wrap gap-2">
              {levels.map((level) => (
                <Button
                  key={level.value}
                  type="button"
                  size="sm"
                  variant={selectedLevel === level.value ? "secondary" : "outline"}
                  className="rounded-full"
                  onClick={() => setSelectedLevel(level.value)}
                >
                  {level.label}
                </Button>
              ))}
            </div>

            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject</div>
            <div className="flex flex-wrap gap-2 overflow-y-auto">
              {subjects.map((subject) => (
                <Button
                  key={subject}
                  type="button"
                  size="sm"
                  variant={selectedSubject === subject ? "secondary" : "ghost"}
                  className="rounded-full"
                  onClick={() => setSelectedSubject(subject)}
                >
                  {subject}
                </Button>
              ))}
            </div>

            <div className="mt-auto space-y-3 border-t border-border/70 pt-4">
              {user ? (
                <Button asChild className="w-full rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/chat">Open full workspace</Link>
                </Button>
              ) : (
                <>
                  <Button asChild className="w-full rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to={ctaHref}>Create free account</Link>
                  </Button>
                  <Button asChild variant="ghost" className="w-full justify-center gap-2 rounded-xl text-muted-foreground">
                    <Link to="/auth"><LogIn className="h-4 w-4" /> Sign in</Link>
                  </Button>
                </>
              )}
              <button
                type="button"
                onClick={() => (user ? setShowUpgradeModal(true) : setShowSignupModal(true))}
                className="w-full text-center text-xs text-muted-foreground transition-colors hover:text-primary"
              >
                View plans & pricing
              </button>
              <div className="flex justify-center gap-3 text-[11px] text-muted-foreground">
                <Link to="/privacy-policy" className="hover:text-foreground">Privacy</Link>
                <Link to="/terms-of-service" className="hover:text-foreground">Terms</Link>
                <Link to="/cookie-policy" className="hover:text-foreground">Cookies</Link>
              </div>
            </div>
          </div>
        </aside>

        {/* Workspace */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between px-4 py-2 sm:px-6 sm:py-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground"
                onClick={() => setSidebarOpen((open) => !open)}
                aria-label={sidebarOpen ? "Hide menu" : "Show menu"}
                aria-expanded={sidebarOpen}
              >
                {sidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
              </Button>
              <span className="text-sm text-muted-foreground">{selectedSubject} · {selectedLevel}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-muted-foreground sm:inline">{remaining === null ? "Signed in" : `${remaining} free messages left`}</span>
              {!user && (
                <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to={ctaHref}>Sign up</Link>
                </Button>
              )}
            </div>
          </div>

          <div ref={transcriptRef} className="flex-1 overflow-y-auto px-4 sm:px-8">
            <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col py-2 sm:py-6">
              {messages.length === 0 ? (
                <div className="my-auto text-center">
                  <img src={tutorMark} alt="Best Virtual Tutor" width={72} height={72} className="mx-auto mb-5 hidden h-18 w-18 object-contain sm:block" />
                  <h1 className="font-display text-2xl font-bold text-foreground sm:text-4xl">What would you like to learn?</h1>
                  <p className="mt-1.5 hidden text-sm text-muted-foreground sm:mt-3 sm:block sm:text-base">
                    A patient AI tutor for any subject, at any level — from first steps to advanced work.
                  </p>
                  <div className="mt-3 sm:mt-8">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Explore a new way to learn</h2>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3 sm:gap-3">
                      {learningPaths.map((path) => (
                        <button
                          key={path.name}
                          type="button"
                          onClick={() => {
                            window.fbq?.("trackCustom", "LearningPathSelected", { option: path.name });
                            setInput(path.prompt);
                            inputRef.current?.focus();
                          }}
                          className="rounded-xl border border-border/70 bg-card/60 p-2 text-left transition-colors hover:border-primary/60 hover:bg-card/90 sm:p-3"
                        >
                          <img
                            src={path.image}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="mx-auto mb-2 h-16 w-16 rounded-xl object-cover sm:mb-3 sm:h-28 sm:w-full"
                          />
                          <span className="block text-sm font-semibold text-foreground">{path.name}</span>
                          <span className="mt-1 block text-xs text-muted-foreground">{path.blurb}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="my-auto space-y-6" role="log" aria-live="polite">
                  {messages.map((message, index) => (
                    <Message from={message.role} key={`${message.role}-${index}`}>
                      <MessageContent className={message.role === "user" ? "bg-primary text-primary-foreground" : "max-w-2xl bg-transparent px-0 py-0 text-base"}>
                        {message.role === "assistant" ? <MessageResponse>{message.content}</MessageResponse> : message.content}
                      </MessageContent>
                    </Message>
                  ))}
                  {isLoading && messages.at(-1)?.role !== "assistant" && <Shimmer className="text-sm">Thinking…</Shimmer>}
                </div>
              )}
            </div>
          </div>

          <div className="px-4 pb-4 sm:px-8 sm:pb-5">
            <div className="mx-auto w-full max-w-3xl">
              <PromptInput onSubmit={({ text }) => send(text)} className="rounded-2xl border-border bg-card/80 shadow-2xl backdrop-blur-xl focus-within:border-primary/60">
                <PromptInputTextarea
                  ref={inputRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="Ask anything you want to learn…"
                  className="min-h-14 p-3 text-base sm:min-h-24 sm:p-5"
                  disabled={isLoading}
                />
                <PromptInputFooter className="border-t border-border px-3 py-3">
                  <PromptInputTools>
                    <PromptInputButton
                      type="button"
                      tooltip="Voice chat unlocks after sign up"
                      onClick={() => setShowSignupModal(true)}
                      aria-label="Use voice chat after signing up"
                    >
                      <Mic className="h-4 w-4" />
                      <span className="hidden sm:inline">Voice</span>
                    </PromptInputButton>
                  </PromptInputTools>
                  <PromptInputSubmit
                    status={isLoading ? "submitted" : "ready"}
                    disabled={isLoading || !input.trim()}
                    className="rounded-lg bg-primary text-primary-foreground"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </PromptInputSubmit>
                </PromptInputFooter>
              </PromptInput>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Buddy can make mistakes — check anything important.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
